import { useEffect, useState } from 'react'

import CardSurface from '../card-surface/CardSurface'
import { ResidentSecurityDeposit } from '../../_types/resident'
import { getMyDeposit } from '../../_data/resident'
import {
  DeductionRow,
  DepositAmount,
  DepositHeading,
  DepositRow,
  DepositStack,
  ReturnText,
  StatusBadge,
  SubLabel,
} from './styled'

interface DepositCardProps {
  tenancyId: string
}

const formatMoney = (cents: number, currency: string): string =>
  `$${(cents / 100).toFixed(2)} ${currency.toUpperCase()}`

/**
 * The resident's security-deposit card. Shows what the property manager is
 * holding, any move-out deductions (so a deduction is never a surprise), and the
 * amount returned once it's settled. Renders nothing until a deposit is on file
 * (status not "none"); the PM's internal notes are stripped server-side.
 *
 * @param {DepositCardProps} props - the resident's tenancy.
 * @returns {JSX.Element | null}
 */
const DepositCard = ({ tenancyId }: DepositCardProps): JSX.Element | null => {
  const [deposit, setDeposit] = useState<ResidentSecurityDeposit | null>(null)

  useEffect(() => {
    let isMounted = true
    void getMyDeposit(tenancyId).then(loaded => {
      if (isMounted === false) {
        return
      }
      setDeposit(loaded !== null && loaded.status !== 'none' ? loaded : null)
    })
    return () => {
      isMounted = false
    }
  }, [tenancyId])

  if (deposit === null) {
    return null
  }

  return (
    <CardSurface padding="standard">
      <DepositStack>
        <DepositHeading>Security deposit</DepositHeading>
        <DepositRow>
          <DepositAmount>
            {formatMoney(deposit.amountCents, deposit.currency)}
          </DepositAmount>
          <StatusBadge tone={deposit.status === 'returned' ? 'returned' : 'held'}>
            {deposit.status}
          </StatusBadge>
        </DepositRow>

        {deposit.deductions.length > 0 ? (
          <DepositStack>
            <SubLabel>Deductions</SubLabel>
            {deposit.deductions.map(deduction => (
              <DeductionRow key={deduction.id}>
                <span>{deduction.description}</span>
                <span>{formatMoney(deduction.amountCents, deposit.currency)}</span>
              </DeductionRow>
            ))}
          </DepositStack>
        ) : null}

        {deposit.status === 'returned' ? (
          <ReturnText>
            Returned to you: {formatMoney(deposit.returnedAmountCents, deposit.currency)}
          </ReturnText>
        ) : null}
      </DepositStack>
    </CardSurface>
  )
}

export default DepositCard
